<template>
  <div class="container-fluid">
    <div class="title-container">
      <h3 class="title">
        Sales report historical
      </h3>
      <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          size="sm"
          switch
          v-model="showLastMonth"
          class="switch"
        >Show last month</b-form-checkbox>
      </div>
    </div>
    <div class="d-flex align-items-center mb-3">
      <span class="mr-3">Revenue by:</span>
      <b-form-checkbox
        v-for="(groupData, groupName) in revenueGroups"
        :key="groupName"
        v-model="groupData.visible"
        size="sm" switch class="switch mr-4"
      >{{ groupData.label }}</b-form-checkbox>
    </div>
    <div class="d-flex align-items-center mb-3">
      <span class="mr-3">New projects count by:</span>
      <b-form-checkbox
        v-for="(groupData, groupName) in newProjectsGroups"
        :key="groupName"
        v-model="groupData.visible"
        size="sm" switch class="switch mr-4"
      >{{ groupData.label }}</b-form-checkbox>
    </div>

    <div v-if="isDataPageLoading" style="width: 100%; padding:20px; text-align: center;">
      <Preloader />
      <span style="font-size: 16px;">Data is loading</span>
    </div>
    <template v-else>
      <div ref="tableContain" class="bg-white round">
        <b-table-simple
          class="sales-report-historical table__theme-default slideout__table position-relative"
          :fixed="false"
          hover
          stripped
          responsive
          bordered
        >
          <colgroup class="group-border"><col></colgroup>
          <colgroup class="group-border"><col></colgroup>
          <colgroup class="group-border"><col></colgroup>
          <template v-for="(groupData, groupName) in groups">
            <colgroup v-if="groupData.visible" :key="groupName" class="group-border">
              <col v-for="(headerName, i) in getHeaderNames(groupName)" :key="groupName + i">
            </colgroup>
          </template>
          <b-thead head-variant="light">
            <tr
              class="group-border"
              :class="{'sticky-head': getCoordinates > 0}"
              style="padding: 0px; height: 29px !important;"
              :style="{'top': getCoordinates - 1 + 'px'}">
              <th colspan="3">&nbsp;</th>
              <template v-for="(groupData, groupName) in groups">
                <th
                  v-if="groupData.visible"
                  :key="groupName"
                  class="text-uppercase"
                  :colspan="getHeaderNames(groupName) ? getHeaderNames(groupName).length : 1">
                  {{ groupData.label }} ({{groupData.type.replace('_', ' ')}})
                </th>
              </template>
            </tr>
            <tr
              class="group-border"
              :class="{'sticky-head': getCoordinates > 0}"
              style="padding: 0px; height: 45px !important;"
              :style="{'top': getCoordinates + 25 + 'px'}">
              <th class="date-column text-uppercase">date</th>
              <th class="text-uppercase">revenue</th>
              <th class="text-uppercase">new projects</th>
              <template v-for="(groupData, groupName) in groups">
                <template v-for="(headerName, i) in getHeaderNames(groupName)">
                  <th v-if="groupData.visible" :key="groupName + i" :class="`column-` + groupData.type">
                    <template v-if="revenueGroups.includes(groupName)">
                      <TableUserItem :user="getUserByName(headerName)" view="name-only" />
                    </template>
                    <template v-else>
                      {{headerName}}
                    </template>
                  </th>
                </template>
              </template>
            </tr>
          </b-thead>
          <b-tbody v-if="reportData.data.length">
            <b-tr class="table-row all-time-row group-border">
              <b-td>all time</b-td>
              <b-td>{{ reportData.summary.revenue }}</b-td>
              <b-td class="column-new_projects">
                <span
                  v-b-tooltip.hover.html="{ variant: 'base' }"
                  :title="reportData.summary.new_projects.projects_list.join(', ')">
                  {{ reportData.summary.new_projects.value }}
                </span>
              </b-td>
              <template v-for="(groupData, groupName) in groups">
                <template v-for="(headerName, i) in getHeaderNames(groupName)">
                  <b-td v-if="groupData.visible" :key="groupName + i" :class="`column-` + groupData.type">
                    <template v-if="groupData.type === 'revenue' && reportData.summary[groupName][headerName]">
                      {{ reportData.summary[groupName][headerName] }}
                    </template>
                    <template v-else-if="groupData.type === 'new_projects' && reportData.summary[groupName][headerName]">
                      <span
                        v-b-tooltip.hover.html="{ variant: 'base' }"
                        :title="reportData.summary[groupName][headerName]['projects_list'].join(', ')">
                        {{ reportData.summary[groupName][headerName]['value'] }}
                      </span>
                    </template>
                    <template v-else>0</template>
                  </b-td>
                </template>
              </template>
            </b-tr>
            <b-tr class="table-row group-border" v-for="yearData in reportData.data" :key="yearData.year">
              <b-td class="date-column font-weight-bold">{{ yearData.year }}</b-td>
              <b-td>{{ yearData.summary.revenue }}</b-td>
              <b-td class="column-new_projects">
                <span
                  v-b-tooltip.hover.html="{ variant: 'base' }"
                  :title="yearData.summary.new_projects.projects_list.join(', ')">
                  {{ yearData.summary.new_projects.value }}
                </span>
              </b-td>
              <template v-for="(groupData, groupName) in groups">
                <template v-for="(headerName, i) in getHeaderNames(groupName)">
                  <b-td v-if="groupData.visible" :key="groupName + i" :class="`column-` + groupData.type">
                    <template v-if="groupData.type === 'revenue' && yearData.summary[groupName][headerName]">
                      {{ yearData.summary[groupName][headerName] ? yearData.summary[groupName][headerName] : 0 }}
                    </template>
                    <template v-else-if="groupData.type === 'new_projects' && yearData.summary[groupName][headerName]">
                      <span
                        v-b-tooltip.hover.html="{ variant: 'base' }"
                        :title="yearData.summary[groupName][headerName]['projects_list'].join(', ')">
                        {{ yearData.summary[groupName][headerName]['value'] }}
                      </span>
                    </template>
                    <template v-else>0</template>
                  </b-td>
                </template>
              </template>
            </b-tr>
            <template v-for="yearData in reportData.data">
              <b-tr
                v-for="(monthData, index) in yearData.data"
                :key="monthData.month"
                class="month-row"
                :class="{'group-border_bottom': index == yearData.data.length - 1}">
                <b-td>{{ monthData.month }}</b-td>
                <b-td>{{ monthData.data.revenue }}</b-td>
                <b-td class="column-new_projects">
                  <span
                    v-b-tooltip.hover.html="{ variant: 'base' }"
                    :title="monthData.data.new_projects.projects_list.join(', ')">
                  {{ monthData.data.new_projects.value }}
                  </span>
                </b-td>
                <template v-for="(groupData, groupName) in groups">
                  <template v-for="(headerName, i) in getHeaderNames(groupName)">
                    <b-td v-if="groupData.visible" :key="groupName + i" :class="`column-` + groupData.type">
                      <template v-if="groupData.type === 'revenue' && monthData.data[groupName][headerName]">
                        {{ monthData.data[groupName][headerName] }}
                      </template>
                      <template v-else-if="groupData.type === 'new_projects' && monthData.data[groupName][headerName]">
                        <span
                          v-b-tooltip.hover.html="{ variant: 'base' }"
                          :title="monthData.data[groupName][headerName]['projects_list'].join(', ')">
                          {{ monthData.data[groupName][headerName]['value'] }}
                        </span>
                      </template>
                      <template v-else>0</template>
                    </b-td>
                  </template>
                </template>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </div>
      <div class="bg-white round p-2">
      <h3 class="title">Charts</h3>
      <template v-for="(groupData, groupName) in groups">
        <div v-show="groupData.visible" class="row mt-4" :key="groupName">
          <div class="col-12 pb-4">
            <div class="sales-report-chart-title">{{ groupData.label }} ({{groupData.type.replace('_', ' ')}})</div>
            <canvas :id="`chart-` + groupName">Your browser does not support the canvas element.</canvas>
          </div>
        </div>
      </template>
    </div>
    </template>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { permissions } from '@mocks/permissions'
import StickyHeaderMixin from '../mixins/StickyHeaderMixin'
import Chart from 'chart.js'
import Preloader from '@components/Preloader'
import TableUserItem from '@components/tables/TableUserItem'
import { mapGetters } from 'vuex'

export default {
  name: 'FinanceSalesReportHistoricalPage',
  mixins: [ StickyHeaderMixin ],
  components: {
    Preloader,
    TableUserItem
  },
  data () {
    return {
      permissions: permissions,
      isDataPageLoading: false,
      showLastMonth: false,
      groups: {
        business_developers_revenue: { label: 'business development managers', type: 'revenue', visible: true },
        pre_sales_managers_revenue: { label: 'pre-sales managers', type: 'revenue', visible: true },
        sales_channels_revenue: { label: 'sales channels', type: 'revenue', visible: true },
        business_developers_new_projects: { label: 'business development managers', type: 'new_projects', visible: false },
        pre_sales_managers_new_projects: { label: 'pre-sales managers', type: 'new_projects', visible: false },
        sales_channels_new_projects: { label: 'sales channels', type: 'new_projects', visible: false }
      },
      reportData: {
        'data': [],
        'summary': {}
      },
      charts: [],
      profitMarginChart: null,
      revenueProfitChart: null
    }
  },
  computed: {
    ...mapGetters({
      _getAllUsers: 'getAllUsers'
    }),
    revenueGroups () {
      return Object.values(this.groups).filter(group => group.type === 'revenue')
    },
    newProjectsGroups () {
      return Object.values(this.groups).filter(group => group.type === 'new_projects')
    },
    getDataForChart: () => function (groupName) {
      const monthData = []
      const reportData = this.reportData.data
      for (const yi in reportData) {
        const year = reportData[yi].year
        const yearData = reportData[yi].data
        for (const mi in yearData) {
          const data = cloneDeep(yearData[mi].data[groupName])
          monthData.push({ month: yearData[mi].month, year, data: data })
        }
      }
      return monthData.reverse()
    },
    getHeaderNames: () => function (block) {
      if (this.reportData.summary[block]) {
        return Object.keys(this.reportData.summary[block]).sort((a, b) => {
          const indexA = ['no presales'].indexOf(a)
          const indexB = ['no presales'].indexOf(b)
          if (indexA !== -1) {
            return 1
          } else if (indexB !== -1) {
            return -1
          } else {
            return a - b
          }
        })
      } else {
        return []
      }
    }
  },
  watch: {
    async showLastMonth (value) {
      await this.fetchReportData(value)
      this.charts.forEach(chart => chart.destroy())
      this.createCharts()
    }
  },
  async created () {
    this.isDataPageLoading = true
    await this.$api.users.getAllUsers()
    await this.fetchReportData(this.showLastMonth)
    this.createCharts()
  },
  methods: {
    async fetchReportData (showLastMonth = false) {
      return this._toggleDataPageLoading(async () => {
        await this.$api.finances
          .getReportsSalesHistorical({ 'show-last-month': showLastMonth })
          .then((response) => {
            this.reportData = response.data
          })
      })
    },
    getUserByName (userName) {
      const user = this._getAllUsers.find(user => user.name === userName)
      if (!user) {
        return { name: userName }
      }
      return user
    },
    createCharts () {
      for (const groupName in this.groups) {
        const group = this.groups[groupName]
        this.createChart(groupName, group.type)
      }
    },
    createChart (groupName, groupType) {
      const chartCont = document.getElementById(`chart-` + groupName).getContext('2d')
      const chartData = this.getDataForChart(groupName)

      const monthsLabels = chartData.map(monthData => monthData.month)

      const colors = ['#fd7f6f', '#7eb0d5', '#bd7ebe', '#ffb55a', '#beb9db', '#fdcce5', '#8bd3c7', '#b2e061', '#ffee65']
      const items = this.getHeaderNames(groupName)
      const datasets = []

      for (const i in items) {
        const itemName = items[i]

        const itemData = chartData.map(monthData => {
          if (monthData.data[itemName]) {
            return monthData.data[itemName]['value'] ?? monthData.data[itemName]
          }
          return 0
        })
        const itemColor = colors.shift()

        datasets.push({
          label: itemName,
          data: itemData,
          stack: 'month',
          backgroundColor: itemColor,
          borderColor: itemColor,
          borderWidth: 1
        })
      }

      this.charts.push(new Chart(chartCont, {
        type: 'bar',
        data: {
          labels: monthsLabels,
          datasets: datasets
        },
        options: {
          legend: {
            labels: {
              fontSize: 16
            }
          },
          scales: {
            yAxes: [{
              display: true,
              scaleLabel: {
                display: true,
                labelString: groupType === 'new_projects' ? 'Projects count' : 'Revenue',
                fontSize: 14
              }
            }]
          }
        }
      }))
    }
  }
}
</script>

<style lang="scss">
  div.sales-report-historical{
    table.table {
      width: max-content;
    }
    table.table .group-border {
      border-color: #AAAAAA;
      border-style: solid;
      border-width: 2px;
      &_bottom {
        border-color: #AAAAAA;
        border-style: solid;
        border-width: 0 0 2px 0;
      }
    }
    .all-time-row {
      font-weight:bold;
    }
    table.b-table thead.thead-light tr {
      background-color: #dee2e6;
    }
    table.b-table thead tr th {
      text-transform: none;
      padding: 4px;
    }
    tbody td, thead tr th {
      text-align: center;
      vertical-align: middle;
      max-width: 90px;
    }
    thead tr th.column-new_projects {
      width: 60px;
    }
    tbody tr td.column-new_projects {
      cursor: default;
    }
    tbody td, thead tr th, thead tr th div.user-name, thead tr th div.simple-name{
      font-size: 12px;
    }
    thead tr th.text-uppercase{
      font-size: 11px;
    }
    tbody tr .date-column{
      min-width:80px;
    }
    thead tr.sticky-head {
      position: sticky;
      z-index: 1;
    }
    thead tr.sticky-head.group-border::after {
      border: 1px solid #AAAAAA;
      content: '';
      position: absolute;
      left: 0;
      width: 100%;
    }
  }
  .sales-report-chart-title {
    font-size: 20px;
    font-weight: bold;
    text-align: center;
  }
  .sales-report-chart-title::first-letter {
    text-transform: uppercase;
  }
</style>
